import { DatePicker } from "@/ds/custom/DatePicker";
import { LoadingState } from "@/ds/custom/LoadingState";
import { SectionRow } from "@/ds/custom/SectionRow";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Button } from "@/ds/shadcn/components/ui/button";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { useBulkTransactions } from "@/features/collections/api/useBulkTransactions";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import { usePreferredPriceSource } from "@/features/common/api/usePreferredPriceSource";
import { DataView } from "@/features/common/components/dataview/DataView";
import { ListItem, ListItemMenu } from "@/features/common/components/dataview/ListItem";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { VariantSelector } from "@/features/common/components/VariantSelector";
import { useProduct, type GetProductAndVariantsMarketPriceResponseModel, type VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { emptyBulkTransactionsDraft, useBulkTransactionsState, type BulkTransactionsDraft, type BulkTransactionsEntry } from "@/lib/state/BulkTransactionState";
import { DropdownMenuGroup, DropdownMenuItem } from "@/ds/shadcn/components/ui/dropdown-menu";
import dayjs from "dayjs";
import { ChevronDownIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { GalleryItem, GalleryItemMenu, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { ScreenDivider } from "@/features/common/components/ScreenDivider";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";

type SearchDestination = "Leaving" | "Entering";

export const PageMassTransactionEntry: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;
	const bulkTransactions = useBulkTransactions();
	useNotifyMutationSuccess(bulkTransactions, "Transactions Recorded");
	const preferredCurrency = usePreferredCurrency();
	const preferredPriceSource = usePreferredPriceSource();
	const ref = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const [resetModalVisible, setResetModalVisible] = useState(false);
	const [searchDestination, setSearchDestination] = useState<SearchDestination | null>(null);

	const {
		state,
		dispatchAddLeavingItem,
		dispatchAddEnteringItem,
		dispatchRemoveLeavingItem,
		dispatchRemoveEnteringItem,
		dispatchSetLeavingItemQuantity,
		dispatchSetEnteringItemQuantity,
		dispatchSetCashLeaving,
		dispatchSetCashEntering,
		dispatchSetOccurredDate,
		dispatchReset
	} = useBulkTransactionsState();

	const draft = useMemo(() => state[collectionId] || emptyBulkTransactionsDraft(), [state, collectionId, emptyBulkTransactionsDraft]);

	const onConfirm = useCallback(() => {
		if (!preferredCurrency.data || !preferredPriceSource.data) {
			return;
		}

		bulkTransactions.mutate({
			collectionId,
			requestModel: {
				currency: preferredCurrency.data.currency,
				priceSource: preferredPriceSource.data.priceSource,
				itemsLeaving: draft.itemsLeaving.map(x => ({ variantId: x.variantId, quantity: x.quantity })),
				itemsEntering: draft.itemsEntering.map(x => ({ variantId: x.variantId, quantity: x.quantity })),
				cashLeaving: draft.cashLeaving,
				cashEntering: draft.cashEntering,
				occurredDate: draft.occurredDate
			}
		});
	}, [bulkTransactions, preferredCurrency.data?.currency, preferredPriceSource.data?.priceSource, collectionId, draft]);

	const onReset = useCallback(() => {
		dispatchReset(collectionId);
		bulkTransactions.reset();
		setResetModalVisible(false);
	}, [dispatchReset, collectionId]);

	useEffect(() => {
		if (!bulkTransactions.isSuccess) {
			return;
		}

		dispatchReset(collectionId);
		setTimeout(() => navigate("../../edit"), 300);
	}, [bulkTransactions.isSuccess]);

	const onVariantSelected = useCallback((product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => {
		if (!searchDestination) {
			return;
		}

		if (searchDestination === "Leaving") {
			dispatchAddLeavingItem(
				collectionId,
				variant.variantId,
				product.productId,
				1
			);
		}

		if (searchDestination === "Entering") {
			dispatchAddEnteringItem(
				collectionId,
				variant.variantId,
				product.productId,
				1
			);
		}

		setSearchDestination(null);
	}, [searchDestination, collectionId]);

	return (
		<>
			<HeaderBarTools>
				<div className="flex flex-col @md:flex-row @md:justify-end gap-4">
					<Button variant="outline" className="w-full @md:w-26" onClick={() => setResetModalVisible(true)}>
						Reset
					</Button>

					<Button disabled={!preferredCurrency.data || !preferredPriceSource.data || bulkTransactions.isPending || bulkTransactions.isSuccess} variant="outline" className="w-full @md:w-26" onClick={onConfirm}>
						{bulkTransactions.isPending ? <Spinner /> : <>Confirm</>}
					</Button>
				</div>
			</HeaderBarTools>

			<SplitScreen fixed visible={!!searchDestination} onVisibleChange={visible => !visible && setSearchDestination(null)}>
				<SplitScreenLeftPanel>
					{
						bulkTransactions.error &&
						<div className="p-3 border-b border-t">
							<ErrorDisplay subtle={true} error={bulkTransactions.error} />
						</div>
					}

					<MassTransactionDatePickerDisplay
						draft={draft}
						onDateSelected={date => dispatchSetOccurredDate(collectionId, dayjs(date).format("YYYY-MM-DD"))} />

					<ScreenDivider
						childrenLeft={
							<>
								<MassTransactionCashOptionsDisplay
									title="Cash Leaving"
									prefix="Leaving"
									cash={draft.cashLeaving}
									onClick={value => dispatchSetCashLeaving(collectionId, value)} />

								<MassTransactionAddProduct
									title="Products Leaving"
									buttonText="Add Product"
									items={draft.itemsLeaving}
									onAdd={() => setSearchDestination("Leaving")}
									onDelete={variantId => dispatchRemoveLeavingItem(collectionId, variantId)}
									onSetQuantity={(variantId, quantity) => dispatchSetLeavingItemQuantity(collectionId, variantId, quantity)} />
							</>
						}
						childrenRight={
							<>
								<MassTransactionCashOptionsDisplay
									title="Cash Entering"
									prefix="Entering"
									cash={draft.cashEntering}
									onClick={value => dispatchSetCashEntering(collectionId, value)} />

								<MassTransactionAddProduct
									title="Products Entering"
									buttonText="Add Product"
									items={draft.itemsEntering}
									onAdd={() => setSearchDestination("Entering")}
									onDelete={variantId => dispatchRemoveEnteringItem(collectionId, variantId)}
									onSetQuantity={(variantId, quantity) => dispatchSetEnteringItemQuantity(collectionId, variantId, quantity)} />
							</>
						} />
				</SplitScreenLeftPanel>
				<SplitScreenRightPanel ref={ref}>
					{
						searchDestination &&
						<VariantSelector
							key={searchDestination}
							onSelect={onVariantSelected}
							onCancel={() => setSearchDestination(null)}
							scrollRef={ref} />
					}
				</SplitScreenRightPanel>
			</SplitScreen>

			<ModalConfirm
				open={resetModalVisible}
				onOpenChange={() => setResetModalVisible(false)}
				onConfirm={onReset}
				title="Reset"
				description="Are you sure you want to reset your mass transaction entry for this collection?"
				confirmText="Confirm" />
		</>
	);
};

interface MassTransactionDatePickerDisplayProps {
	draft: BulkTransactionsDraft;
	onDateSelected: (date: Date) => void;
}

const MassTransactionDatePickerDisplay = (props: MassTransactionDatePickerDisplayProps) => {
	const {
		draft,
		onDateSelected
	} = props;

	return (
		<SectionRow
			title="Mass Transaction Entry"
			description="Record a box opening or trade with ease. All transactions will be recorded against this date.">
			<DatePicker
				value={dayjs(draft.occurredDate).toDate()}
				onDateSelected={onDateSelected}>
				<Button variant="outline" className="w-full @md:w-56">
					<div className="flex w-full items-center">
						<div className="flex-1 text-center">
							{dayjs(draft.occurredDate).format("DD MMM YYYY")}
						</div>

						<ChevronDownIcon className="ml-auto" />
					</div>
				</Button>
			</DatePicker>
		</SectionRow>
	);
};

interface MassTransactionCashOptionsDisplayProps {
	title: string;
	prefix: string;
	cash: number;
	onClick: (value: number) => void;
}

const MassTransactionCashOptionsDisplay = (props: MassTransactionCashOptionsDisplayProps) => {
	const {
		title,
		prefix,
		cash,
		onClick
	} = props;

	return (
		<SectionRow title={title}>
			<NumericInput
				className="w-full @md:w-56"
				value={cash}
				onChange={onClick}
				prefix={prefix}
				maxDecimalPlaces={2}
				minValue={0}
				maxValue={1000000} />
		</SectionRow>
	);
};

interface MassTransactionAddProductProps {
	title: string;
	buttonText: string;
	items: BulkTransactionsEntry[];
	onAdd: () => void;
	onDelete: (variantId: string) => void;
	onSetQuantity: (variantId: string, quantity: number) => void;
}

const MassTransactionAddProduct = (props: MassTransactionAddProductProps) => {
	const {
		title,
		buttonText,
		items,
		onAdd,
		onDelete,
		onSetQuantity
	} = props;

	return (
		<>
			<SectionRow title={title}>
				<Button className="w-full @md:w-56" variant="outline" onClick={onAdd}>
					{buttonText}
				</Button>
			</SectionRow>

			{
				items.length > 0 &&
				<DataView
					items={items}
					getKey={x => x.variantId}
					listViewHeaderLabels={["Product", "Quantity", ""]}
					listViewHeaderClassNames={["min-w-0", "w-46", "w-16"]}
					listComponent={PageMassTransactionEntryListView}
					galleryComponent={PageMassTransactionEntryGalleryView}
					galleryMinWidth={192}
					additionalProps={{ onSetQuantity, onDelete }} />
			}
		</>
	);
};

interface PageMassTransactionEntryProps extends BulkTransactionsEntry {
	onSetQuantity: (variantId: string, quantity: number) => void;
	onDelete: (variantId: string) => void;
}

const PageMassTransactionEntryListView: FC<PageMassTransactionEntryProps> = props => {
	const {
		productId,
		variantId,
		quantity,
		onSetQuantity,
		onDelete
	} = props;

	const product = useProduct(productId);
	const variant = useMemo(() => product.data?.variants.find(x => x.variantId === variantId), [product.data?.variants, variantId]);

	if (!variant || !product.data) {
		return (
			<ListItem>
				<TableCell colSpan={3}>
					<div className="p-3 flex justify-center">
						<Spinner />
					</div>
				</TableCell>
			</ListItem>
		);
	}

	return (
		<ListItem>
			<TableCell className="min-w-0">
				<SmallProductDisplay
					productName={product.data.name}
					fcId={variant.fcId}
					imageUrl={product.data.imageUrl} />
			</TableCell>

			<TableCell className="w-4">
				<NumericInput
					value={quantity}
					onChange={value => onSetQuantity(variantId, value)}
					minValue={1}
					maxValue={10000}
					maxDecimalPlaces={0} />
			</TableCell>

			<TableCell className="w-4">
				<ListItemMenu>
					<DropdownMenuGroup>
						<DropdownMenuItem
							variant="destructive"
							onClick={() => onDelete(variantId)}>
							<Trash2Icon />
							Delete
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</ListItemMenu>
			</TableCell>
		</ListItem>
	);
};

const PageMassTransactionEntryGalleryView: FC<PageMassTransactionEntryProps> = props => {
	const {
		productId,
		variantId,
		quantity,
		onSetQuantity,
		onDelete
	} = props;

	const product = useProduct(productId);
	const variant = useMemo(() => product.data?.variants.find(x => x.variantId === variantId), [product.data?.variants, variantId]);

	if (!product.data || !variant) {
		return <LoadingState />;
	}

	return (
		<GalleryItem
			title={product.data.name}
			description={variant.fcId}
			imageUrl={product.data.imageUrl}>
			<GalleryItemMenu>
				<DropdownMenuGroup>
					<DropdownMenuItem
						variant="destructive"
						onClick={() => onDelete(variantId)}>
						<Trash2Icon />
						Delete
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</GalleryItemMenu>

			<GalleryItemProperty>
				<NumericInput
					value={quantity}
					onChange={value => onSetQuantity(variantId, value)}
					minValue={1}
					maxValue={10000}
					maxDecimalPlaces={0} />
			</GalleryItemProperty>
		</GalleryItem>
	);
};
